import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import NotFound from '@/pages/NotFound';
import { SEO_TRADES } from '@/data/seo/trades';
import {
  STATES, REGIONS, stateBySlug, stateLabel, statePath, statesInRegion, licenseNote, UsState,
} from '@/data/seo/states';
import {
  breadcrumbSchema, faqSchema, serviceSchema, itemListSchema, platformFaqs, brandFaqs, Faq, stateEntitySchema, speakableSchema,
} from '@/lib/seoSchema';
import {
  Breadcrumbs, PageHero, AnswerBlock, Section, FaqSection, LinkGrid, CtaBand, EntityFacts, PageWrap, TrustStrip,
} from '@/components/seo/SeoBlocks';
import { LEAD_PRICE, money, TRADES, Lang } from '@/data/constants';
import LiveProjects from '@/components/seo/LiveProjects';
import { statewideMoneyPages } from '@/data/seo/moneyPages';
import { StateAlertForm, TopCitiesSection } from '@/components/seo/StatePageBlocks';


/** State-specific AEO FAQs. Licensing answer comes from the state catalog. */
const stateFaqs = (st: UsState, lang: Lang): Faq[] => {
  const name = stateLabel(st, lang);
  const price = money(LEAD_PRICE);
  return brandFaqs(
    lang === 'es'
      ? [
          { q: `¿Qué proyectos de construcción hay en ${name}?`, a: `GIGGONE muestra proyectos reales publicados en ${name} por propietarios, contratistas generales y propietarios comerciales — techado, concreto, electricidad, plomería, HVAC, remodelación y más. El oficio, el tipo de proyecto, el presupuesto y la ciudad son visibles sin costo.` },
          { q: `¿Cómo consigo trabajo de construcción en ${name}?`, a: `Cree una cuenta de subcontratista, elija sus oficios, filtre el marketplace por ${name} y desbloquee los proyectos dentro de su área con un lead de su membresía o un crédito individual de ${price}.` },
          { q: `¿Cuánto cuesta un lead de construcción en ${name}?`, a: `Cada lead cuesta ${price}, el mismo precio fijo en los 50 estados y D.C. El estado, el oficio o el presupuesto no cambian el precio.` },
          { q: `¿Necesito licencia de contratista en ${name}?`, a: licenseNote(st, lang) },
          { q: `¿Publicar un proyecto en ${name} cuesta algo?`, a: 'No. Publicar es gratis para propietarios y contratistas generales en todo Estados Unidos. Solo los contratistas pagan por desbloquear leads.' },
        ]
      : [
          { q: `What construction projects are available in ${name}?`, a: `GIGGONE lists real projects posted in ${name} by homeowners, general contractors and commercial property owners — roofing, concrete, electrical, plumbing, HVAC, remodeling and more. Trade, project type, budget range and city are visible for free.` },
          { q: `How do I find construction work in ${name}?`, a: `Create a subcontractor account, pick your trades, filter the marketplace to ${name} and unlock projects inside your service area with an included membership lead or a ${price} single credit.` },
          { q: `How much does a construction lead cost in ${name}?`, a: `Every lead costs a flat ${price} — the same price in all 50 states and D.C. State, trade and budget never change the price.` },
          { q: `Do I need a contractor license in ${name}?`, a: licenseNote(st, lang) },
          { q: `Does it cost anything to post a project in ${name}?`, a: 'No. Posting is free for homeowners and general contractors everywhere in the United States. Only contractors pay to unlock leads.' },
        ]
  );
};

/* ------------------------------------------------------------------ */
/* STATE LANDING PAGE — /en/states/:code  |  /es/estados/:code          */
/* ------------------------------------------------------------------ */
export const StatePage: React.FC = () => {
  const { code = '' } = useParams();
  const { lang, path, t } = useI18n();
  const st = stateBySlug(code);
  if (!st) return <NotFound />;

  const name = stateLabel(st, lang);
  const faqs = stateFaqs(st, lang);
  const url = path(statePath(st));
  const browseState = `${path('/jobs')}?state=${st.code}`;

  const marketTradeLinks = TRADES.map((tr) => ({
    label: lang === 'es' ? `${tr.es} en ${name}` : `${tr.en} in ${name}`,
    to: `${path('/jobs')}?state=${st.code}&trade=${tr.slug}`,
  }));
  // Statewide money pages (e.g. /leads/roofing-leads-texas) — one per money trade.
  const leadLinks = statewideMoneyPages(st.code).map((m) => ({
    label: lang === 'es' ? `Leads de ${m.trade.es} en ${name}` : `${m.trade.en} Leads in ${name}`,
    to: path(`/leads/${m.slug}`),
  }));

  const tradeGuides = SEO_TRADES.slice(0, 24).map((x) => ({
    label: lang === 'es' ? x.es : x.en,
    to: path(`/trades/${x.slug}`),
  }));
  const regionLinks = statesInRegion(st.region)
    .filter((x) => x.code !== st.code)
    .map((x) => ({ label: stateLabel(x, lang), to: path(statePath(x)) }));
  const region = REGIONS.find((r) => r.key === st.region);

  const intro =
    lang === 'es'
      ? `Proyectos de construcción activos en ${name}. Propietarios y contratistas generales publican el trabajo gratis; los contratistas con membresía desbloquean la dirección exacta, los contactos y los planos por ${money(LEAD_PRICE)} por lead.`
      : `Active construction projects across ${name}. Homeowners and general contractors post work for free; contractors with a membership unlock the exact address, contacts and plan documents for a flat ${money(LEAD_PRICE)} per lead.`;

  return (
    <>
      <Seo
        title={lang === 'es' ? `Proyectos y Leads de Construcción en ${name}` : `${name} Construction Projects & Contractor Leads`}
        description={
          lang === 'es'
            ? `Proyectos de construcción reales en ${name}: todos los oficios, leads a ${money(LEAD_PRICE)} y publicación gratis para propietarios y contratistas generales.`
            : `Real construction projects in ${name} — every trade, flat ${money(LEAD_PRICE)} leads, and free project posting for homeowners and general contractors.`
        }
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: `/${lang}` },
            { name: lang === 'es' ? 'Estados' : 'States', url: path('/states') },
            { name, url },
          ]),
          stateEntitySchema(st),
          serviceSchema({ trade: 'Construction project leads', area: st.en, description: intro, url }),
          faqSchema(faqs),
          itemListSchema(`${st.en} construction trades`, marketTradeLinks.map((m) => ({ name: m.label, url: m.to }))),
          itemListSchema(`${st.en} statewide construction lead pages`, leadLinks.map((m) => ({ name: m.label, url: m.to }))),
          speakableSchema(url, name),
        ]}

      />

      <PageHero
        eyebrow={lang === 'es' ? 'Cobertura nacional' : 'Nationwide coverage'}
        title={lang === 'es' ? `Construcción en ${name}` : `${name} Construction Marketplace`}
        intro={intro}
      >
        <Link to={browseState} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {lang === 'es' ? `Ver proyectos en ${name}` : `Browse ${name} projects`}
        </Link>
        <Link to={path('/register')} className="rounded-xl border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10">
          {t('seo.ctaSubBtn')}
        </Link>
      </PageHero>

      <PageWrap>
        <Breadcrumbs
          items={[
            { name: t('nav.home'), url: path('/') },
            { name: lang === 'es' ? 'Estados' : 'States', url: path('/states') },
            { name },
          ]}
        />

        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />

        <EntityFacts
          rows={[
            { k: lang === 'es' ? 'Estado' : 'State', v: `${name} (${st.code})` },
            { k: lang === 'es' ? 'Región' : 'Region', v: region ? (lang === 'es' ? region.es : region.en) : '—' },
            { k: t('seo.factPrice'), v: money(LEAD_PRICE) },
            { k: t('seo.factPost'), v: t('seo.factFree') },
          ]}
        />

        {st.hub && (
          <Link
            to={path(st.hub)}
            className="mt-6 flex items-center justify-between gap-3 rounded-2xl border border-orange-200 bg-orange-50 p-4 text-sm font-bold text-slate-900 hover:border-orange-300"
          >
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-orange-600" aria-hidden />
              {lang === 'es' ? `Explorar ${name} por condado y ciudad` : `Explore ${name} by county and city`}
            </span>
            <ArrowRight className="h-4 w-4 text-orange-600" aria-hidden />
          </Link>
        )}

        <LiveProjects state={st.code} limit={9} heading={t('seo.recentProjects', { place: name })} />

        <TopCitiesSection st={st} />

        <StateAlertForm st={st} />

        <Section title={lang === 'es' ? `Licencias en ${name}` : `Licensing in ${name}`}>
          <p>{licenseNote(st, lang)}</p>
          <TrustStrip />
        </Section>

        <LinkGrid
          title={lang === 'es' ? `Leads de construcción en todo ${name}` : `Statewide ${name} construction leads`}
          links={leadLinks}
          icon="trade"
        />
        <LinkGrid title={lang === 'es' ? `Proyectos en ${name} por oficio` : `${name} projects by trade`} links={marketTradeLinks} icon="trade" />
        <LinkGrid title={t('seo.allTrades')} links={tradeGuides} icon="trade" />


        <FaqSection faqs={faqs} />

        <LinkGrid
          title={lang === 'es' ? `Otros estados del ${region?.es ?? ''}` : `Other ${region?.en ?? ''} states`}
          links={regionLinks}
          icon="map"
        />
        <Link to={path('/states')} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-orange-600 hover:underline">
          {lang === 'es' ? 'Todos los estados' : 'All U.S. states'} <ArrowRight className="h-4 w-4" />
        </Link>

        <CtaBand />
      </PageWrap>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* STATES INDEX — /en/states  |  /es/estados                            */
/* ------------------------------------------------------------------ */
export const StatesHub: React.FC = () => {
  const { lang, path, t } = useI18n();
  const faqs = platformFaqs(lang);
  const all = STATES.map((s) => ({ name: stateLabel(s, lang), url: path(statePath(s)) }));

  return (
    <>
      <Seo
        title={lang === 'es' ? 'Proyectos de Construcción por Estado — EE. UU.' : 'Construction Projects by State — All 50 States + D.C.'}
        description={
          lang === 'es'
            ? `Explore proyectos de construcción y leads para contratistas en los 50 estados y Washington, D.C. Leads a ${money(LEAD_PRICE)}, publicación gratis.`
            : `Browse construction projects and contractor leads in all 50 states and Washington, D.C. Flat ${money(LEAD_PRICE)} leads, free project posting.`
        }
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: `/${lang}` },
            { name: lang === 'es' ? 'Estados' : 'States', url: path('/states') },
          ]),
          itemListSchema('U.S. states', all),
          faqSchema(faqs),
        ]}
      />
      <PageHero
        eyebrow={lang === 'es' ? 'Cobertura nacional' : 'Nationwide coverage'}
        title={lang === 'es' ? 'Proyectos de construcción en Estados Unidos' : 'Construction Projects Across the U.S.'}
        intro={
          lang === 'es'
            ? 'GIGGONE conecta a propietarios, contratistas generales y subcontratistas en los 50 estados y Washington, D.C. Elija un estado para ver proyectos activos, oficios y preguntas frecuentes.'
            : 'GIGGONE connects homeowners, general contractors and subcontractors in all 50 states and Washington, D.C. Pick a state to see live projects, trades and answers to common questions.'
        }
      >
        <Link to={path('/jobs')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {lang === 'es' ? 'Ver leads en Estados Unidos' : 'Browse All U.S. Leads'}
        </Link>
      </PageHero>
      <PageWrap>
        <Breadcrumbs items={[{ name: t('nav.home'), url: path('/') }, { name: lang === 'es' ? 'Estados' : 'States' }]} />
        <AnswerBlock question={faqs[0].q} answer={faqs[0].a} />
        <LiveProjects limit={9} heading={lang === 'es' ? 'Proyectos recientes en EE. UU.' : 'Recent projects across the U.S.'} />
        {REGIONS.map((r) => (
          <LinkGrid
            key={r.key}
            title={lang === 'es' ? r.es : r.en}
            icon="map"
            links={statesInRegion(r.key).map((s) => ({ label: stateLabel(s, lang), to: path(statePath(s)) }))}
          />
        ))}
        <FaqSection faqs={faqs} />
        <CtaBand />
      </PageWrap>
    </>
  );
};

export default StatePage;
